import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, ShoppingBag, Package, Clock, Store, Truck, AlertTriangle, ImageOff, Tag, PackageX, ChevronRight, Sparkles, Trash2 } from "lucide-react";
import { api, peso } from "../../lib/api";
import { Button } from "../../components/ui/button";
import EnhancePhotosDialog from "./EnhancePhotosDialog";
import ResetSalesDialog from "./ResetSalesDialog";
import { useAuth } from "../../context/AuthContext";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[#E1E5DF] rounded-lg p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4 text-white" strokeWidth={2} />
        </div>
      </div>
      <div className="font-heading font-extrabold text-3xl text-[#1C2826] mt-2 tracking-tight">{value}</div>
    </div>
  );
}

function AuditCard({ icon: Icon, theme, label, count, items, expanded, onToggle, testid }) {
  // Themes pre-declared so Tailwind doesn't purge dynamic classes.
  const themes = {
    red:    { border: "border-[#E74C3C]",  iconBg: "bg-[#E74C3C]/10", iconText: "text-[#E74C3C]" },
    orange: { border: "border-[#E67E22]",  iconBg: "bg-[#E67E22]/10", iconText: "text-[#E67E22]" },
    purple: { border: "border-[#8E44AD]",  iconBg: "bg-[#8E44AD]/10", iconText: "text-[#8E44AD]" },
  };
  const t = themes[theme] || themes.red;
  return (
    <div className={`rounded-xl border-2 transition bg-white ${count > 0 ? t.border : "border-[#E1E5DF] opacity-70"}`} data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 text-left disabled:cursor-not-allowed"
        disabled={count === 0}
        data-testid={`${testid}-toggle`}
      >
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${t.iconBg}`}>
          <Icon className={`h-5 w-5 ${t.iconText}`} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-2xl text-[#1C2826] tracking-tight" data-testid={`${testid}-count`}>{count}</div>
          <div className="text-xs uppercase tracking-wider font-bold text-[#4A5D5A]">{label}</div>
        </div>
        {count > 0 && (
          <ChevronRight className={`h-4 w-4 text-[#738380] transition-transform ${expanded ? "rotate-90" : ""}`} />
        )}
      </button>
      {expanded && count > 0 && (
        <div className="border-t border-[#E1E5DF] divide-y divide-[#F1F3EE] max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
          {items.slice(0, 50).map((p) => (
            <Link
              key={p.id}
              to={`/admin/products?edit=${p.id}`}
              className="p-3 flex items-center gap-3 hover:bg-[#F8F9F5]"
              data-testid={`${testid}-item-${p.id}`}
            >
              {p.image_url ? (
                <img src={p.image_url} alt="" className="h-9 w-9 rounded object-cover bg-[#F1F3EE] flex-shrink-0" />
              ) : (
                <div className="h-9 w-9 rounded bg-[#F1F3EE] flex items-center justify-center flex-shrink-0">
                  <ImageOff className="h-4 w-4 text-[#A0AAA7]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#1C2826] line-clamp-1">{p.name}</div>
                <div className="text-[11px] text-[#738380]">{p.category} · {peso(p.price)} · {p.stock} in stock</div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#A0AAA7] flex-shrink-0" />
            </Link>
          ))}
          {items.length > 50 && (
            <div className="p-3 text-center text-xs text-[#738380]">+{items.length - 50} more · open Products page to see all</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminOverview() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [lowStock, setLowStock] = useState({ items: [], count: 0, threshold: 5 });
  const [audit, setAudit] = useState(null);
  const [openAudit, setOpenAudit] = useState(null);  // which audit card is expanded
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/orders").then((r) => setRecent(r.data.slice(0, 8)));
    api.get("/dashboard/low-stock").then((r) => setLowStock(r.data));
    api.get("/dashboard/quality-audit").then((r) => setAudit(r.data)).catch(() => setAudit(null));
  }, []);

  if (!stats) return <div className="py-20 flex justify-center"><div className="h-8 w-8 border-4 border-[#2F5233] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Low stock alert banner */}
      {lowStock.count > 0 && (
        <div className="bg-[#FFF4E5] border-l-4 border-[#E67E22] rounded-lg p-4 flex items-start gap-3" data-testid="low-stock-alert">
          <AlertTriangle className="h-5 w-5 text-[#E67E22] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-[#1C2826]">
              {lowStock.count} product{lowStock.count > 1 ? "s" : ""} running low
            </div>
            <div className="text-sm text-[#4A5D5A]">
              Stock is at or below {lowStock.threshold} units. Restock soon to avoid lost sales.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Today's Sales" value={peso(stats.today_revenue)} accent="bg-[#2F5233]" />
        <StatCard icon={ShoppingBag} label="Orders Today" value={stats.today_count} accent="bg-[#E67E22]" />
        <StatCard icon={Clock} label="Active Orders" value={stats.pending} accent="bg-[#F39C12]" />
        <StatCard icon={Package} label="Low Stock" value={stats.low_stock} accent="bg-[#E74C3C]" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Total Revenue" value={peso(stats.revenue)} accent="bg-[#2F5233]" />
        <StatCard icon={ShoppingBag} label="Total Orders" value={stats.total_orders} accent="bg-[#2F5233]" />
        <StatCard icon={Truck} label="Online Orders" value={stats.online_orders} accent="bg-[#8E44AD]" />
        <StatCard icon={Store} label="POS Sales" value={stats.pos_orders} accent="bg-[#E67E22]" />
      </div>

      {/* Image quality audit — flags products that aren't customer-ready */}
      {audit && (
        <div className="bg-white border border-[#E1E5DF] rounded-lg p-4 sm:p-5" data-testid="quality-audit">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-bold text-lg text-[#1C2826]">Storefront Quality Audit</div>
              <div className="text-xs text-[#738380]">Products that aren't customer-ready. Click any card to see them and fix in 1 click.</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setEnhanceOpen(true)}
                className="h-8 bg-[#E67E22] hover:bg-[#B86316] text-white text-xs"
                data-testid="ai-enhance-photos-btn"
                title="Replace staff-uploaded photos with studio-quality AI photos"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1" /> AI Enhance Photos
              </Button>
              {isAdmin && (
                <Button
                  onClick={() => setResetOpen(true)}
                  variant="outline"
                  className="h-8 border-[#C62828] text-[#C62828] hover:bg-[#C62828] hover:text-white text-xs"
                  data-testid="reset-sales-btn"
                  title="Wipe trial sales data — admin only, irreversible"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Reset Sales Data
                </Button>
              )}
              {audit.missing_photo.count + audit.placeholder_price.count + audit.out_of_stock.count === 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-[#EAF5EC] text-[#2F5233] font-bold uppercase tracking-wider" data-testid="audit-all-clear">All clear ✓</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <AuditCard
              icon={ImageOff}
              theme="purple"
              label="Missing photo"
              count={audit.missing_photo.count}
              items={audit.missing_photo.items}
              expanded={openAudit === "photo"}
              onToggle={() => setOpenAudit(openAudit === "photo" ? null : "photo")}
              testid="audit-missing-photo"
            />
            <AuditCard
              icon={Tag}
              theme="orange"
              label="Placeholder price (≤ ₱1)"
              count={audit.placeholder_price.count}
              items={audit.placeholder_price.items}
              expanded={openAudit === "price"}
              onToggle={() => setOpenAudit(openAudit === "price" ? null : "price")}
              testid="audit-placeholder-price"
            />
            <AuditCard
              icon={PackageX}
              theme="red"
              label="Out of stock (no variants)"
              count={audit.out_of_stock.count}
              items={audit.out_of_stock.items}
              expanded={openAudit === "stock"}
              onToggle={() => setOpenAudit(openAudit === "stock" ? null : "stock")}
              testid="audit-out-of-stock"
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent orders */}
        <div className="bg-white border border-[#E1E5DF] rounded-lg">
          <div className="p-5 border-b border-[#E1E5DF]">
            <div className="font-heading font-bold text-lg text-[#1C2826]">Recent orders</div>
          </div>
          <div className="divide-y divide-[#E1E5DF]">
            {recent.map((o) => (
              <div key={o.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-heading font-semibold text-[#1C2826]">{o.number}</div>
                  <div className="text-xs text-[#738380]">{o.customer_name} · {new Date(o.created_at).toLocaleString("en-PH")}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#4A5D5A]">{o.status.replace(/_/g, " ")}</span>
                  <span className="font-heading font-extrabold text-[#2F5233]">{peso(o.total)}</span>
                </div>
              </div>
            ))}
            {recent.length === 0 && <div className="p-6 text-center text-sm text-[#738380]">No orders yet.</div>}
          </div>
        </div>

        {/* Low stock list */}
        <div className="bg-white border border-[#E1E5DF] rounded-lg" data-testid="low-stock-list">
          <div className="p-5 border-b border-[#E1E5DF] flex items-center justify-between">
            <div className="font-heading font-bold text-lg text-[#1C2826]">Low stock</div>
            <span className="text-xs text-[#738380]">≤ {lowStock.threshold} units</span>
          </div>
          <div className="divide-y divide-[#E1E5DF] max-h-[480px] overflow-y-auto">
            {lowStock.items.length === 0 && (
              <div className="p-6 text-center text-sm text-[#738380]">All stock levels are healthy ✓</div>
            )}
            {lowStock.items.map((p) => (
              <div key={p.id} className="p-4 flex items-center gap-3" data-testid={`low-stock-${p.id}`}>
                <img src={p.image_url} alt="" className="h-10 w-10 rounded object-cover bg-[#F1F3EE] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-[#1C2826] line-clamp-1">{p.name}</div>
                  <div className="text-xs text-[#738380]">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</div>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${p.stock === 0 ? "bg-[#E74C3C] text-white" : "bg-[#FFF4E5] text-[#E67E22]"}`}>
                  {p.stock === 0 ? "Out of stock" : `${p.stock} left`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {enhanceOpen && (
        <EnhancePhotosDialog
          open={enhanceOpen}
          onClose={() => setEnhanceOpen(false)}
          onDone={() => api.get("/dashboard/quality-audit").then((r) => setAudit(r.data)).catch(() => {})}
        />
      )}
      {resetOpen && (
        <ResetSalesDialog
          open={resetOpen}
          onClose={() => setResetOpen(false)}
          onDone={() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }}
        />
      )}
    </div>
  );
}
